# Wellness Frontier Privacy Policy

**Effective date:** September 1, 2026  
**Last updated:** August 31, 2026

Wellness Frontier is operated by Advanced Health and Wellness Group
(“Wellness Frontier,” “we,” “us,” or “our”). Your employer makes Wellness
Frontier available to you, but using it is your choice.

This policy explains what information Wellness Frontier collects, how we use
it, what your employer can and cannot see, and the choices you have.

## The short version

- We do not sell your personal information or consumer health data.
- We do not use your information for targeted advertising.
- Connecting Apple Health or Health Connect is optional. We read only the
  categories you approve, and you can stop future collection.
- Your employer can see the named points board, limited roster information,
  program participation counts, and certain group-level reports.
- Your employer does not receive your individual health measurements, health
  survey answers, coach questions, benefits questions, medical history, or
  use of sensitive support resources.
- Opening crisis, behavioral-health, substance-use, domestic-violence, or
  employee-assistance resources creates no event, counter, or activity record.

## 1. What this policy covers

This policy covers the Wellness Frontier employee website and mobile apps.
It does not cover your employer’s systems, your health plan, a health care
provider, Apple Health, Health Connect, or another service you choose to use.
Those organizations have their own privacy practices.

The program is not directed to children under 13.

## 2. Information we collect

### Account and workplace information

Your employer may provide your name, work email address, preferred language,
work site, shift, hire date, and an initial department. You may choose a
different department in the app; the department you choose is the one the
program uses.

We also create sign-in, session, security, and audit records needed to operate
and protect your account. These records may include timestamps, app or browser
version, device and network information, and an IP address.

### Health and wellness information

Depending on the features available to you, we may collect:

- steps, sleep duration, and resting heart rate from Apple Health or Health
  Connect after you authorize access;
- answers you choose to give in a health or wellness survey;
- the wellness area you voluntarily select as most important for the month;
- goals you set and program actions you complete; and
- points, achievements, and days-in-a-row progress created through your use of
  the program.

We do not change information in Apple Health or Health Connect.

### Benefits and AI coach information

If your program enables the benefits coach, we process the question you type
and the recent conversation needed to answer it. The coach uses your
employer’s plan documents to explain what those documents say. It is not a
doctor, therapist, lawyer, or insurance representative, and its answers are
not medical or legal advice.

Wellness Frontier does not keep a conversation transcript. We may keep a
de-identified version of a question, without your name, work site, or exact
time, to evaluate and improve the coach. A de-identified record may retain the
employer program involved. We keep it only in a form intended not to identify
you.

The coach uses Microsoft Azure OpenAI. Depending on the Microsoft configuration
approved for a deployment, Microsoft may temporarily retain submitted content
for abuse monitoring under its provider safeguards. That provider copy is not
our conversation transcript and may not be available to us through an account
deletion request. We will update this policy if the contracted configuration
changes this handling in a material way.

### Information we deliberately do not collect

We do not ask for family medical history. We do not collect your precise
location or camera roll. We do not create health conclusions about you from
your browsing behavior. We do not record whether you opened crisis,
behavioral-health, substance-use, domestic-violence, or employee-assistance
resources.

## 3. How we use information

We use information to:

- create and secure your account;
- deliver the wellness program and features you request;
- show your own health-source measurements, goals, progress, and program
  activity;
- calculate points and achievements;
- answer benefits questions when the coach is enabled;
- operate, troubleshoot, protect, and improve the service;
- produce the employer-visible information described below; and
- comply with law and respond to valid legal process.

We do not use consumer health data for advertising, and we do not sell it.

## 4. What your employer can see

Your employer can see:

- your name, points, and place on the points board if you have points;
- how many people joined and average points by department;
- your work site, shift, and hire date, which came from the employer’s records;
- the department you selected and whether it differs from the employer’s
  starting department;
- counts of people who finished a survey, completed a verified program action,
  started a coach conversation, connected a health app, or finished a program
  checklist item;
- those participation counts for the company and, where available, by site,
  shift, length of service, age group, and sex at birth;
- company-wide totals for the monthly wellness-area question; and
- correction records when a challenge or points result is fixed, including who
  made the correction and why.

Your employer does not receive:

- your individual survey answers;
- your steps, sleep, resting heart rate, water entries, or another individual
  number from a connected health source;
- what you ask the coach, what the coach answers, or your benefits questions;
- your doctor visits, lab results, medical history, or care you sought;
- your date of birth;
- the wellness area you personally selected for the month;
- the pages you read, how long you use the app, or how often you open it; or
- whether you opened crisis, behavioral-health, substance-use,
  domestic-violence, or employee-assistance resources.

### Small groups

Some employer reports can contain small counts. The program does not apply one
universal minimum group size to every employer report. A count for some
activities or wellness areas can be one.

The monthly wellness question has an additional rule for the Mental and
Financial areas. If one, two, three, or four people choose either of those
areas, the employer receives no monthly wellness-area numbers for that month.
If nobody chooses one of those areas, a zero may appear. The other five areas
do not use this special rule.

## 5. Connected health sources

Connecting Apple Health or Health Connect is optional. Before the app reads a
supported category, your phone presents the platform’s permission controls.
You can choose categories individually.

You can stop future collection by using the account controls in the app and
the permission controls on your phone. Disconnecting stops new collection; it
does not automatically delete information already sent to Wellness Frontier.
Use the deletion options described below if you also want existing information
removed.

## 6. When we disclose information

We disclose information only as described in this policy:

- **Your employer:** receives only the information listed in Section 4.
- **Service providers:** process information for hosting, databases, storage,
  email delivery, security, support, document processing, search, and the AI
  coach. Our principal hosting and AI provider is Microsoft Azure in the United
  States. Service providers may use information only to perform services for
  us under their contracts.
- **Legal and safety requirements:** we may disclose information when required
  by law, valid legal process, or when reasonably necessary to protect the
  rights, safety, and security of people or the service.
- **Business changes:** if the business is reorganized, financed, sold, or
  transferred, information may be disclosed as part of that transaction,
  subject to applicable law and the promises in this policy.

We do not disclose personal information or consumer health data to data
brokers, advertising networks, or social-media advertising platforms.

## 7. How long we keep information

We keep information only for the periods needed for the purposes described
here, subject to legal and contractual requirements:

- profile information, private survey answers, connected-health measurements,
  goals, and source permissions are generally kept while you are enrolled or
  until they are deleted;
- wellness-score history uses a rolling 52-week window;
- sign-in codes and sessions last minutes to hours and expire automatically;
- audit and security records are generally retained for up to 400 days;
- de-identified coach questions may be retained indefinitely; and
- published group totals may remain after you leave because they do not contain
  your identity.

When deletion applies to archived or backup systems, deletion may occur on the
backup rotation or when a backup is restored, within the period allowed by
applicable law.

## 8. Your choices and privacy rights

Participation is voluntary. You may skip a survey, the monthly question, a
health-source connection, or the entire program. Skipping does not reduce your
pay or employment benefits through Wellness Frontier.

Depending on where you live, you may have the right to ask us to:

- confirm whether we hold information about you;
- provide access to or a copy of your information;
- correct inaccurate information;
- withdraw consent for future collection or disclosure;
- delete information; or
- explain or appeal a refusal to act on a request.

You can disconnect a health source or request account deletion from the app’s
Account area where that control is available. You may also make any privacy
request by emailing **privacy@wellnessfrontier.ai**. We may need to verify your
identity before completing a request. We will respond within the time required
by applicable law and will not unlawfully discriminate against you for using a
privacy right.

Washington residents should also read our separate
**Consumer Health Data Privacy Policy**, which describes rights under the
Washington My Health My Data Act.

## 9. HIPAA and health-plan information

Whether HIPAA applies to a workplace wellness program depends on how the
program is structured. A program offered as part of a group health plan may
involve protected health information governed by HIPAA, while a program
offered directly by an employer may not.

This policy is not a HIPAA Notice of Privacy Practices for your employer’s
health plan or health care provider. If Wellness Frontier handles protected
health information on behalf of a covered health plan, the applicable health
plan documents, Notice of Privacy Practices, and business-associate agreement
also govern that information.

## 10. Security and incidents

We use administrative, technical, and organizational safeguards designed to
protect information. No system can guarantee absolute security.

If a privacy or security incident triggers a notification duty, we will notify
affected people and regulators as required by applicable law.

## 11. Changes to this policy

We may update this policy as the program changes. If a change materially
affects what we collect, how we use it, or what an employer can see, we will
provide notice before the change takes effect when required by law.

## 12. Contact us

For privacy questions or requests, contact:

**Wellness Frontier Privacy**  
Advanced Health and Wellness Group  
**privacy@wellnessfrontier.ai**
